import Link from 'next/link'
import React from 'react'
import {
  HOOD_MAP,
  PILLAR_PAGE_MAP,
  PillarPage,
  SEARCH_PAGE_CITY_MAP,
  SearchHood,
  SearchPageCity
} from 'src/constants/searchPages'
import { cleanAccentLocation } from 'src/utils/address'
import { IConfiguration, ILocation } from '../../types/graphql'

type IProps = {
  location: ILocation
  activeConfig: IConfiguration
  className?: string

  breadcrumbs: [SearchPageCity | null, SearchHood | null]
}

function Breadcrumb({
  location,
  activeConfig,
  className,
  breadcrumbs
}: IProps) {
  const [foundCity, foundHood] = breadcrumbs

  const { addressDetails } = location
  const { city, colonia } = addressDetails ?? {}
  const { pillarPage } = activeConfig
  const { label } = PILLAR_PAGE_MAP[pillarPage as PillarPage] ?? {}

  // Prefer the canonical, accented map name when the crumb resolved to a known
  // city/hood; otherwise clean the raw Google-geocoded value the way the H1
  // does (strips Plus Codes / "Sin Nombre" placeholders / comma-laden
  // addresses). Never render the raw geocoding slug/placeholder as copy.
  const cityLabel = foundCity
    ? SEARCH_PAGE_CITY_MAP[foundCity].cityName
    : (cleanAccentLocation(city) ?? city ?? '')
  // Null hoodLabel → drop the colonia crumb entirely and fall back to the
  // city-terminal crumb, rather than surfacing an unusable placeholder.
  const hoodLabel = foundHood
    ? HOOD_MAP[foundHood].hoodName
    : cleanAccentLocation(colonia)

  const crumbLink =
    'capitalize text-ink-200 transition-colors hover:text-ink-700'
  const sep = (
    <span aria-hidden="true" className="text-ink-100">
      /
    </span>
  )

  // Terminal segment: prefer the colonia (neighborhood) since it actually
  // identifies WHERE this listing sits within the city. We used to render
  // `configLocMap(activeConfig)` here (e.g. "Mini-Bodega"), but that's just
  // the singular form of the pillar above it (`Mini Bodegas / … / Mini-
  // Bodega`) — pure tautology, zero info. When the colonia matches a known
  // SearchHood we make it a link to the hood page; otherwise plain text.
  // City stays as the last clickable crumb when no colonia is available.
  return (
    <nav
      aria-label="Breadcrumb"
      className={`flex flex-wrap items-center gap-x-2.5 gap-y-1 font-display text-[10px] tracking-[0.18em] uppercase md:text-[11px] ${className}`}
    >
      <Link href="/" className={crumbLink}>
        Renta
      </Link>
      {sep}
      <Link href={`/${pillarPage}`} className={crumbLink}>
        {label}
      </Link>
      {sep}
      {hoodLabel ? (
        <>
          {/* `<Link href="#">` resolves to the CURRENT URL on both SSR and
              CSR — but CSR includes window.location.search (query string)
              while SSR has only the pathname. The resulting href bytes
              differ, firing React 19 #418 on listing details pages that
              were navigated to with search-state query params (sliderOpen,
              search, lat, lng, etc.). Confirmed via at-error
              captureHydrationDiff on
              /renta-bodegas-comerciales/ciudad-apodaca/sin-nombre-de-colonia-26/...
              (Chrome Desktop, 2026-06-01). Fix: render plain <span> when
              foundCity is null - no href, no drift. The crumb is
              informational at that point anyway (no matching SearchPageCity
              to link to). See CLAUDE.md "Trigger L". */}
          {foundCity ? (
            <Link href={`/${pillarPage}/${foundCity}`} className={crumbLink}>
              {cityLabel}
            </Link>
          ) : (
            <span className={crumbLink}>{cityLabel}</span>
          )}
          {sep}
          {foundHood ? (
            <Link
              href={`/${pillarPage}/${foundCity}/${foundHood}`}
              className="truncate text-ink-700 capitalize transition-colors hover:text-blue-500"
            >
              {hoodLabel}
            </Link>
          ) : (
            <span className="truncate text-ink-700 capitalize">
              {hoodLabel}
            </span>
          )}
        </>
      ) : (
        <span className="truncate text-ink-700 capitalize">{cityLabel}</span>
      )}
    </nav>
  )
}

export default React.memo(Breadcrumb)
